import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Alert, Grid, Slider, Typography, Box
} from "@mui/material";

export default function AddEditRelationshipModal({ open, onClose, onSave, editData }) {
    const { chatbotId } = useParams();
    const isEdit = !!editData;

    const [formData, setFormData] = useState({
        source_entity: '',
        relation_type: '',
        target_entity: '',
        confidence_score: 0.9,
        valid_from: '',
        valid_until: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (editData) {
            setFormData({
                source_entity: editData.source_entity || '',
                relation_type: editData.relation_type || '',
                target_entity: editData.target_entity || '',
                confidence_score: editData.confidence_score || 0.9,
                valid_from: editData.valid_from ? editData.valid_from.split('T')[0] : '',
                valid_until: editData.valid_until ? editData.valid_until.split('T')[0] : ''
            });
        } else {
            setFormData({
                source_entity: '', relation_type: '', target_entity: '',
                confidence_score: 0.9, valid_from: '', valid_until: ''
            });
        }
        setError('');
    }, [editData, open]);

    const handleChange = (field) => (e) => {
        setFormData(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleSubmit = async () => {
        if (!formData.source_entity.trim() || !formData.relation_type.trim() || !formData.target_entity.trim()) {
            setError('Source Entity, Relation Type, and Target Entity are required.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const payload = {
                ...formData,
                valid_from: formData.valid_from || null,
                valid_until: formData.valid_until || null
            };

            if (isEdit) {
                await axiosInstance.put(`/api/knowledge-graph/${editData.id}`, payload, {
                    params: { chatbot_id: chatbotId }
                });
            } else {
                await axiosInstance.post('/api/knowledge-graph', payload, {
                    params: { chatbot_id: chatbotId }
                });
            }

            onSave();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? 'Edit Relationship' : 'Add New Relationship'}</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth label="Source Entity" required
                            value={formData.source_entity}
                            onChange={handleChange('source_entity')}
                            placeholder="e.g., Navaid"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth label="Relation Type" required
                            value={formData.relation_type}
                            onChange={handleChange('relation_type')}
                            placeholder="e.g., works_at, is_friend_with, manages"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth label="Target Entity" required
                            value={formData.target_entity}
                            onChange={handleChange('target_entity')}
                            placeholder="e.g., Fahad"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ px: 1 }}>
                            <Typography gutterBottom variant="body2">
                                Confidence Score: {(formData.confidence_score * 100).toFixed(0)}%
                            </Typography>
                            <Slider
                                value={formData.confidence_score}
                                onChange={(e, v) => setFormData(prev => ({ ...prev, confidence_score: v }))}
                                min={0} max={1} step={0.05}
                                marks={[{ value: 0, label: '0%' }, { value: 0.5, label: '50%' }, { value: 1, label: '100%' }]}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth label="Valid From" type="date"
                            value={formData.valid_from}
                            onChange={handleChange('valid_from')}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth label="Valid Until" type="date"
                            value={formData.valid_until}
                            onChange={handleChange('valid_until')}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                    {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
